











































































































import { computed, defineComponent, PropType, toRefs, useContext } from '@nuxtjs/composition-api'

import useCarDescriptions from '@/composables/use-car-descriptions'
import useStoreLink from '@/composables/use-store-link'

import DateHourRange from '@/classes/DateHourRange'
import PricedCar from '@/classes/PricedCar'
import Store from '@/classes/Store'

import dataLayer, { DataLayer } from '@/modules/data-layer'
import useBreakpoint from '@/composables/use-breakpoint'

import CarStore from '@/classes/CarStore'

import useI18n from '@/composables/use-i18n'

export default defineComponent({
  props: {
    car: {
      type: PricedCar as PropType<PricedCar | null>,
      default: null,
    },
    store: {
      type: Store as PropType<Store | null>,
      default: null,
    },
    /**
     * 是否為廣告車輛
     */
    isAdPromoted: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    range: {
      type: DateHourRange as PropType<DateHourRange | null>,
      default: null,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
    // 在列表中的順序
    index: {
      type: Number,
    },
    // 推薦系統分辨每次查詢的 query id
    qid: {
      type: String,
    },
    // 推薦系統使用的 ranker name
    rankerName: {
      type: String,
    },
  },
  setup (props, { emit }) {
    const { match } = useBreakpoint()
    const isMobile = !match('lg')

    const { car, store } = toRefs(props)

    const brand = computed(() => car.value?.brand?.name ?? car.value?.brand)
    const model = computed(() => car.value?.model?.name ?? car.value?.model)
    const { searchCarCardDescriptions } = useCarDescriptions(car)

    const formatNumber = new Intl.NumberFormat('en-US').format
    const price = computed(() => car.value?.price)
    const originalPrice = computed(() => car.value?.originalPrice)

    const storeLink = useStoreLink(store)

    /** 當 `store` 為 `nullish` 時意味著這是模糊搜尋的結果，並且只顯示部分詳情 */
    const isBrief = computed(() => !props.store)

    /**
     * Car Key，追蹤商家車輛點擊
     *
     * 格式：_s_${storeId}_c_${carId}_no_${index}_
     * s: 商家 id
     * c: 車輛 id
     * no: 車輛在列表中的順序
     */
    const carKey = computed(() => `_s_${props.store?.id ?? ''}_c_${props.car?.id ?? ''}_no_${props.index ?? ''}_`)

    const { app: { localePath }, query } = useContext()
    const confirmLink = computed(() => {
      const path = car.value?.link
      if (!path) return
      return localePath({
        path,
        query: {
          ...query.value,
          // 將排序透傳到 confirm 頁
          no: String(props.index),
          qid: props.qid,
          rn: props.rankerName,
          ap: props.isAdPromoted ? '1' : '0',
          carKey: carKey.value,
        },
      })
    })

    const clickDetail = () => {
      if (confirmLink.value) { window.open(confirmLink.value, '_blank') }

      if (car.value && store.value) {
        dataLayer.selectItem({
          items: [DataLayer.GogooutCarToEcommerceItem(car.value, store.value)],
        })
      }

      emit('click', car.value)
    }

    const onMouseEnter = () => {
      emit('on-hover', new CarStore(props.car!, props.store))
    }

    const onMouseLeave = () => {
      emit('on-hover', undefined)
    }

    const i18n = useI18n()
    const carBadges = computed<{icon: string, name: string}[]>(() => {
      const car = props.car
      if (!car) return []

      const badges = []

      badges.push({ icon: 'icon-car-time', name: i18n.tc('carCard.badge.age', car.age ?? 0, [car.age ?? 0]).toString() })

      if (car.seats) {
        badges.push({ icon: 'icon-seat', name: String(car.seats) })
      }

      if (car.transmission && car.transmission.name) {
        badges.push({ icon: 'icon-gears', name: car.transmission.name })
      }

      return badges
    })

    const selectedPolicy = computed<string[]>(() =>
      props.store
        ? Store.parseSelectedPolicy(props.store, i18n.locale).map(policy => i18n.t(`carCard.store.selectedPolicy.${policy}`).toString())
        : [],
    )
    const selectedService = computed<string[]>(() =>
      props.store ? Store.parseSelectedService(props.store).map(service => service.name) : [],
    )

    return {
      isMobile,

      brand,
      model,
      searchCarCardDescriptions,
      formatNumber,
      price,
      originalPrice,

      storeLink,
      confirmLink,
      isBrief,
      clickDetail,

      onMouseEnter,
      onMouseLeave,

      carBadges,
      selectedPolicy,
      selectedService,
    }
  },
})
